import { useQuery } from '@tanstack/react-query';
import { payments } from '@lawbee/domain';
import { Alert, Button, ErrorState, Field, FormError, Input, LoadingState, Select, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@lawbee/ui';
import { useEffect, useState, type FormEvent } from 'react';
import { PlanGate } from '../../components/PlanGate';
import { api, errorMessage } from '../../lib/api';
import { useApiMutation } from '../../lib/hooks';
import { can, ME_KEY, useMe } from '../../lib/me';
import type { AccountsResponse, Destination, PaymentKind } from '../../lib/types';
import { DestinationBadge, ReadOnlyNote } from '../app/ops/shared';
import { SettingsCard } from './layout';

const ACCOUNTS_KEY = ['payment-accounts'] as const;

export function PaymentsSettingsPage() {
  return (
    <PlanGate feature="payments">
      <PaymentsSettingsInner />
    </PlanGate>
  );
}

function PaymentsSettingsInner() {
  const me = useMe();
  const canEdit = can(me, 'payments_admin');
  const q = useQuery({ queryKey: ACCOUNTS_KEY, queryFn: () => api.get<AccountsResponse>('/api/payments/accounts') });
  if (q.isLoading) return <LoadingState label="Loading payment settings" />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  return (
    <div className="space-y-6">
      <Alert variant="info" title="Trust accounting">
        {q.data.notice || payments.TRUST_COMPLIANCE_NOTICE}
      </Alert>
      {q.data.stripeMode === 'dev' ? <Alert variant="warning">Development mode: payments use a simulator. No real money moves.</Alert> : null}
      {!canEdit ? <ReadOnlyNote>Only an owner can change payment accounts and routing.</ReadOnlyNote> : null}
      <AccountsCard data={q.data} canEdit={canEdit} />
      <RoutingCard data={q.data} canEdit={canEdit} />
    </div>
  );
}

type AccountDraft = { label: string; stripeAccountId: string };

function AccountsCard({ data, canEdit }: { data: AccountsResponse; canEdit: boolean }) {
  const init = (d: Destination): AccountDraft => ({
    label: data.accounts[d]?.label ?? (d === 'trust' ? 'Trust account' : 'Operating account'),
    stripeAccountId: data.accounts[d]?.stripeAccountId ?? '',
  });
  const [trust, setTrust] = useState<AccountDraft>(() => init('trust'));
  const [operating, setOperating] = useState<AccountDraft>(() => init('operating'));
  const [problems, setProblems] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTrust(init('trust'));
    setOperating(init('operating'));
  }, [data.accounts]);

  const save = useApiMutation((body: { trust: { label: string; stripeAccountId: string | null }; operating: { label: string; stripeAccountId: string | null } }) => api.put('/api/payments/accounts', body), {
    success: 'Payment accounts saved',
    invalidate: [ACCOUNTS_KEY],
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const t = { label: trust.label.trim() || 'Trust account', stripeAccountId: trust.stripeAccountId.trim() || null };
    const o = { label: operating.label.trim() || 'Operating account', stripeAccountId: operating.stripeAccountId.trim() || null };
    const found = payments.validateAccounts([
      { destination: 'trust', ...t },
      { destination: 'operating', ...o },
    ]);
    setProblems(found);
    if (found.length) return;
    setError(null);
    save.mutate({ trust: t, operating: o }, { onError: (err) => setError(errorMessage(err)) });
  };

  const block = (d: Destination, value: AccountDraft, set: (v: AccountDraft) => void, help: string) => (
    <fieldset className="space-y-3 rounded-xl border border-border/70 p-4">
      <legend className="flex items-center gap-2 px-1 text-sm font-semibold">
        {d === 'trust' ? 'Trust account' : 'Operating account'} <DestinationBadge destination={d} />
      </legend>
      <p className="text-xs text-muted-foreground">{help}</p>
      <Field label="Label" htmlFor={`acct-${d}-label`}>
        <Input id={`acct-${d}-label`} value={value.label} disabled={!canEdit} onChange={(e) => set({ ...value, label: e.target.value })} />
      </Field>
      <Field label="Stripe account id" htmlFor={`acct-${d}-id`} description="From your Stripe dashboard. Looks like acct_...">
        <Input
          id={`acct-${d}-id`}
          className="figures"
          placeholder="acct_..."
          autoComplete="off"
          value={value.stripeAccountId}
          disabled={!canEdit}
          onChange={(e) => set({ ...value, stripeAccountId: e.target.value })}
        />
      </Field>
    </fieldset>
  );

  return (
    <form onSubmit={submit} noValidate>
      <SettingsCard
        title="Accounts"
        description="Trust and operating money go to separate Stripe accounts so they are never mixed."
        footer={
          canEdit ? (
            <Button type="submit" loading={save.isPending}>
              Save accounts
            </Button>
          ) : undefined
        }
      >
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {block('trust', trust, setTrust, 'Money held for clients, such as retainers for future work.')}
            {block('operating', operating, setOperating, 'Money the firm has earned, such as paid invoices and consultation fees.')}
          </div>
          {problems.length ? (
            <Alert variant="destructive" title="Check your accounts">
              <ul className="list-disc space-y-1 pl-5">
                {problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </Alert>
          ) : null}
          <FormError message={error} />
        </div>
      </SettingsCard>
    </form>
  );
}

function RoutingCard({ data, canEdit }: { data: AccountsResponse; canEdit: boolean }) {
  const [routing, setRouting] = useState<Record<PaymentKind, Destination>>(data.routing);
  useEffect(() => setRouting(data.routing), [data.routing]);
  const dirty = payments.PAYMENT_KINDS.some((k) => routing[k] !== data.routing[k]);
  const save = useApiMutation((body: Record<PaymentKind, Destination>) => api.put('/api/payments/routing', body), {
    success: 'Routing saved',
    invalidate: [ACCOUNTS_KEY, ME_KEY],
  });

  return (
    <SettingsCard
      title="Routing"
      description="Which account each kind of payment goes to. Retainers normally belong in trust."
      footer={
        canEdit ? (
          <Button disabled={!dirty} loading={save.isPending} onClick={() => save.mutate(routing)}>
            Save routing
          </Button>
        ) : undefined
      }
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Payment kind</TableHead>
            <TableHead className="w-48">Goes to</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {payments.PAYMENT_KINDS.map((k) => (
            <TableRow key={k}>
              <TableCell>
                <label htmlFor={`route-${k}`}>{payments.PAYMENT_KIND_LABELS[k]}</label>
                {k === 'retainer' && routing[k] === 'operating' ? (
                  <span className="mt-1 block text-xs text-destructive">Retainers are usually trust money. Check your law society rules.</span>
                ) : null}
              </TableCell>
              <TableCell>
                <Select
                  id={`route-${k}`}
                  className="w-44"
                  value={routing[k]}
                  disabled={!canEdit}
                  onChange={(e) => setRouting((r) => ({ ...r, [k]: e.target.value as Destination }))}
                  options={[
                    { value: 'trust', label: 'Trust account' },
                    { value: 'operating', label: 'Operating account' },
                  ]}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </SettingsCard>
  );
}
